/**
 * Valida dimensões de chapas baseado na espessura
 * Utiliza dimensionLimits do material se disponível, caso contrário usa limites padrão
 *
 * Formato dimensionLimits: "122x244:2,3|300x200:4,6,8,10"
 * - largura x altura : espessuras
 * - Múltiplas configurações separadas por |
 */

export interface DimensionLimits {
  maxWidth: number;
  maxHeight: number;
}

export interface DimensionValidationResult {
  isValid: boolean;
  maxWidth: number;
  maxHeight: number;
  message?: string;
}

interface ParsedDimensionLimit {
  widthCm: number;
  heightCm: number;
  thicknesses: number[];
}

/**
 * Parseia a string de dimensionLimits do material
 * Formatos suportados:
 * - "122x244:2,3" - dimensões com espessuras específicas
 * - "122x244:2,3|300x200:4,6" - múltiplas configurações
 * - "122x244" ou "122x244;" - dimensões para todas espessuras
 * - "122x244:2,3;" - com trailing semicolon
 */
function parseDimensionLimits(dimensionLimits?: string): ParsedDimensionLimit[] {
  if (!dimensionLimits) {
    return [];
  }

  const limits: ParsedDimensionLimit[] = [];

  // Clean up: remove trailing semicolons and split by | or ;
  const cleanedLimits = dimensionLimits.replace(/;+$/, '').trim();
  if (!cleanedLimits) {
    return [];
  }

  // Split by | for multiple groups, or treat as single group
  const groups = cleanedLimits.split(/[|;]/).filter(g => g.trim());

  for (const group of groups) {
    const trimmedGroup = group.trim();
    if (!trimmedGroup) continue;

    // Try format with thicknesses: "122x244:2,3,4"
    const matchWithThicknesses = trimmedGroup.match(/^(\d+)x(\d+):(\d+(?:,\d+)*)$/);
    if (matchWithThicknesses) {
      const widthCm = parseInt(matchWithThicknesses[1], 10);
      const heightCm = parseInt(matchWithThicknesses[2], 10);
      const thicknesses = matchWithThicknesses[3].split(',').map((t) => parseInt(t, 10));

      limits.push({ widthCm, heightCm, thicknesses });
      continue;
    }

    // Try format without thicknesses: "122x244" (applies to all thicknesses)
    const matchWithoutThicknesses = trimmedGroup.match(/^(\d+)x(\d+)$/);
    if (matchWithoutThicknesses) {
      const widthCm = parseInt(matchWithoutThicknesses[1], 10);
      const heightCm = parseInt(matchWithoutThicknesses[2], 10);
      // Empty thicknesses array means "all thicknesses"
      limits.push({ widthCm, heightCm, thicknesses: [] });
    }
  }

  return limits;
}

/**
 * Retorna os limites padrão baseado na espessura (fallback)
 * Usado quando material não tem dimensionLimits configurado
 */
function getDefaultDimensions(thicknessMm: number): DimensionLimits {
  let dimension1: number;
  let dimension2: number;

  // 2mm e 3mm: até 1×2m OU 1.22×2.44m
  if (thicknessMm === 2 || thicknessMm === 3) {
    dimension1 = 1.22;
    dimension2 = 2.44;
  }
  // 3, 4, 6, 8, 10mm: até 3×2m
  else if ([3, 4, 6, 8, 10].includes(thicknessMm)) {
    dimension1 = 3.0;
    dimension2 = 2.0;
  }
  // 12mm+: até 1×2m
  else if (thicknessMm >= 12) {
    dimension1 = 1.0;
    dimension2 = 2.0;
  }
  // Padrão para espessuras não especificadas
  else {
    dimension1 = 3.0;
    dimension2 = 2.0;
  }

  // Garante que maxWidth (Largura) seja sempre o menor e maxHeight (Altura) o maior
  return {
    maxWidth: Math.min(dimension1, dimension2),
    maxHeight: Math.max(dimension1, dimension2)
  };
}

/**
 * Retorna os limites máximos de dimensão baseado na espessura
 * Utiliza dimensionLimits do material se disponível
 */
export function getMaxDimensions(
  thicknessMm: number,
  dimensionLimits?: string
): DimensionLimits {
  const parsedLimits = parseDimensionLimits(dimensionLimits);

  // Busca configuração específica para esta espessura
  for (const limit of parsedLimits) {
    // Empty thicknesses array means "all thicknesses"
    const matchesThickness = limit.thicknesses.length === 0 || limit.thicknesses.includes(thicknessMm);

    if (matchesThickness) {
      // Converte centímetros para metros e garante ordem correta
      const widthM = limit.widthCm / 100;
      const heightM = limit.heightCm / 100;

      return {
        maxWidth: Math.min(widthM, heightM),
        maxHeight: Math.max(widthM, heightM)
      };
    }
  }

  // Fallback para limites padrão se não encontrou configuração
  return getDefaultDimensions(thicknessMm);
}

/**
 * Valida se as dimensões são válidas para a espessura especificada
 *
 * @param widthCm - Largura em centímetros
 * @param heightCm - Altura em centímetros
 * @param thicknessMm - Espessura em milímetros
 * @param dimensionLimits - String de limites do material (opcional)
 */
export function validateDimensions(
  widthCm: number,
  heightCm: number,
  thicknessMm: number,
  dimensionLimits?: string
): DimensionValidationResult {
  // Convert cm to meters for comparison
  const widthMeters = widthCm / 100;
  const heightMeters = heightCm / 100;

  const limits = getMaxDimensions(thicknessMm, dimensionLimits);

  // Verifica se largura e altura estão dentro dos limites do eixo correspondente
  const isValid = widthMeters <= limits.maxWidth && heightMeters <= limits.maxHeight;

  if (!isValid) {
    return {
      isValid: false,
      maxWidth: limits.maxWidth,
      maxHeight: limits.maxHeight,
      message: `Para espessura ${thicknessMm}mm, dimensões máximas são ${(limits.maxWidth * 100).toFixed(0)}cm × ${(limits.maxHeight * 100).toFixed(0)}cm`
    };
  }

  return {
    isValid: true,
    maxWidth: limits.maxWidth,
    maxHeight: limits.maxHeight
  };
}

/**
 * Retorna mensagem de erro amigável para dimensões inválidas
 */
export function getDimensionErrorMessage(
  thicknessMm: number,
  dimensionLimits?: string
): string {
  const limits = getMaxDimensions(thicknessMm, dimensionLimits);

  return `Chapas de ${thicknessMm}mm: dimensões máximas ${(limits.maxWidth * 100).toFixed(0)}cm × ${(limits.maxHeight * 100).toFixed(0)}cm`;
}
